import { createContext, useContext, useState, useEffect } from "react";
import api, { setAuthTokens, clearAuthTokens, getAccessToken } from "@/utils/api";

const UserContext = createContext(null);

export function UserProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const saved = localStorage.getItem("if_user");
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });

  // Validate session on mount: if we have a token, fetch /me; otherwise clear stale user.
  useEffect(() => {
    const token = getAccessToken();
    if (!token && user) {
      setUser(null);
      localStorage.removeItem("if_user");
      return;
    }
    if (token) {
      api.get("/auth/me")
        .then((r) => {
          setUser(r.data);
          localStorage.setItem("if_user", JSON.stringify(r.data));
        })
        .catch(() => {
          // Token invalid → wipe
          clearAuthTokens();
          setUser(null);
          localStorage.removeItem("if_user");
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * login() now accepts the full auth response: { access_token, refresh_token, user }
   * Backwards-compat: if only a user object is passed (legacy), keep that shape.
   */
  const login = (authResponse) => {
    if (authResponse?.access_token) {
      setAuthTokens(authResponse.access_token, authResponse.refresh_token);
      setUser(authResponse.user);
      localStorage.setItem("if_user", JSON.stringify(authResponse.user));
    } else {
      // legacy fallback
      setUser(authResponse);
      localStorage.setItem("if_user", JSON.stringify(authResponse));
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    clearAuthTokens();
    setUser(null);
    localStorage.removeItem("if_user");
  };

  const updateUser = (updates) => {
    const updated = { ...user, ...updates };
    setUser(updated);
    localStorage.setItem("if_user", JSON.stringify(updated));
  };

  return (
    <UserContext.Provider value={{ user, login, logout, updateUser }}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  return useContext(UserContext);
}
